'use client'

import React, { useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useBranding } from '@/hooks/useBranding'

function normalizeEmail(s: string) {
  return (s || '').trim().toLowerCase()
}

type AuthCardVariant = 'inline' | 'modal'
type AuthCardChrome = 'glass' | 'none'

export type AuthCardProps = {
  sellerId?: number
  variant?: AuthCardVariant
  chrome?: AuthCardChrome
  theme?: 'light' | 'dark'
  next?: string
  onRequestSignup?: () => void
  onSuccess?: () => void
  initialMode?: 'password' | 'otp'
  companyStatus?: number
  isWhitelabel?: boolean
}

type Mode = 'password' | 'otp'

function OtpBoxes({
  value,
  onChange,
  length = 6,
  disabled,
  theme = 'dark',
}: {
  value: string
  onChange: (v: string) => void
  length?: number
  disabled?: boolean
  theme?: 'light' | 'dark'
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([])

  function setAt(i: number, ch: string) {
    const digits = value.split('')
    digits[i] = ch
    const next = digits.join('').replace(/\D/g, '').slice(0, length)
    onChange(next)
  }

  function focus(i: number) {
    refs.current[i]?.focus()
    refs.current[i]?.select()
  }

  return (
    <div className="grid grid-cols-6 gap-2">
      {Array.from({ length }).map((_, i) => {
        const digit = value[i] ?? ''
        return (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el
            }}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d*"
            disabled={disabled}
            value={digit}
            onChange={(e) => {
              const d = e.target.value.replace(/\D/g, '').slice(-1) // último dígito
              setAt(i, d)
              if (d && i < length - 1) focus(i + 1)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace') {
                e.preventDefault()
                if (value[i]) {
                  setAt(i, '')
                } else if (i > 0) {
                  focus(i - 1)
                  setAt(i - 1, '')
                }
              }
              if (e.key === 'ArrowLeft' && i > 0) focus(i - 1)
              if (e.key === 'ArrowRight' && i < length - 1) focus(i + 1)
            }}
            onPaste={(e) => {
              e.preventDefault()
              const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
              if (!pasted) return
              onChange(pasted)
              const nextFocus = Math.min(pasted.length, length - 1)
              setTimeout(() => focus(nextFocus), 0)
            }}
            className={cn(
            'h-12 w-full rounded-xl text-center text-lg outline-none transition',
            theme === 'dark'
                ? 'bg-white/5 border border-white/10 focus:ring-2 focus:ring-white/15'
                : 'bg-black/5 border border-black/10 focus:ring-2 focus:ring-black/10'
            )}
          />
        )
      })}
    </div>
  )
}

export function AuthCard({
  sellerId = 0,
  variant = 'inline',
  chrome = 'glass',
  theme = 'dark',
  next = '/start',
  onRequestSignup,
  onSuccess,
  initialMode = 'password',
  companyStatus = 0,
  isWhitelabel = false,
}: AuthCardProps) {

  const router = useRouter()

  const { brand } = useBranding(sellerId)

  const [mode, setMode] = useState<Mode>('password')

  // shared
  const [email, setEmail] = useState('')
  const emailNorm = useMemo(() => normalizeEmail(email), [email])

  // password
  const [password, setPassword] = useState('')
  const [pLoading, setPLoading] = useState(false)
  const [pErr, setPErr] = useState<string | null>(null)

  // otp
  const [otpSent, setOtpSent] = useState(false)
  const [code, setCode] = useState('')
  const [oLoading, setOLoading] = useState(false)
  const [oErr, setOErr] = useState<string | null>(null)

  async function loginPassword() {
    setPErr(null)
    setPLoading(true)
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNorm, password }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || 'LOGIN_FAILED')

      onSuccess?.()

      router.replace(next)
      router.refresh()
    } catch (e: any) {
      setPErr(e?.message || 'LOGIN_FAILED')
    } finally {
      setPLoading(false)
    }
  }

  async function startOtp() {
    setOErr(null)
    setOLoading(true)
    try {
      const res = await fetch('/api/auth/otp/login/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNorm }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || 'OTP_START_FAILED')
      setOtpSent(true)
      setCode('')
    } catch (e: any) {
      setOErr(e?.message || 'OTP_START_FAILED')
    } finally {
      setOLoading(false)
    }
  }

  async function verifyOtp() {
    setOErr(null)
    setOLoading(true)
    try {
      const res = await fetch('/api/auth/otp/login/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNorm, code }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || 'OTP_VERIFY_FAILED')

      onSuccess?.()

      router.replace(next)
      router.refresh()
    } catch (e: any) {
      setOErr(e?.message || 'OTP_VERIFY_FAILED')
    } finally {
      setOLoading(false)
    }
  }

  const wrapperClass =
  chrome === 'glass'
    ? cn(
        'w-full rounded-3xl bg-white/90 dark:bg-white/5 backdrop-blur-xl',
        'shadow-[0_20px_60px_rgba(0,0,0,0.10)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.45)]',
        variant === 'inline' ? 'max-w-md p-5 sm:p-6' : 'max-w-lg p-5 sm:p-6'
      )
    : cn('w-full') // ✅ inline limpio

  return (
    <div className={wrapperClass}>
      {/* Top */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
            <div className="text-xl font-semibold tracking-tight truncate text-foreground dark:text-foreground">
              {brand?.name ?? 'Welcome'}
            </div>
        </div>

        {/* pill switch */}
        <div className="shrink-0 rounded-full bg-black/5 dark:bg-white/10 p-1">
          <button
            type="button"
            onClick={() => {
              setMode('password')
              setOtpSent(false)
              setOErr(null)
            }}
            className={cn(
              'px-3 py-1.5 text-xs rounded-full transition',
              mode === 'password'
                ? 'bg-white dark:bg-white/15 text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Password
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('otp')
              setPErr(null)
            }}
            className={cn(
              'px-3 py-1.5 text-xs rounded-full transition',
              mode === 'otp'
                ? 'bg-white dark:bg-white/15 text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            OTP
          </button>
        </div>
      </div>

      {/* Subtitle */}
      <div className="mt-3 text-sm text-muted-foreground">
        Entra rápido y seguro. Si no recuerdas tu contraseña, usa OTP.
      </div>

      {/* Form */}
      <form
        className="mt-5 space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (!emailNorm) return
          if (mode === 'password') {
            if (pLoading || !password) return
            loginPassword()
            return
          }
          // mode === 'otp'
          if (!otpSent) {
            if (oLoading) return
            startOtp()
            return
          }
          // otpSent === true (verificar)
          if (oLoading || code.length !== 6) return
          verifyOtp()
        }}
      >

        {mode === 'password' && (
          <>
          <Button
            type="button"
            className="h-12 w-full rounded-lg flex items-center justify-center gap-3 bg-white text-black hover:bg-gray-50 border"
            variant="secondary"
            onClick={() => {
              const u = new URL('/api/auth/google/start', window.location.origin)
              u.searchParams.set('next', next)
              u.searchParams.set('sellerId', String(Number(sellerId || 0) || 0))
              u.searchParams.set('approveId', String(Number(companyStatus || 0) || 0))
              u.searchParams.set('whitelabel', isWhitelabel ? '1' : '0')
              window.location.href = u.toString()
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 48 48"
            >
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.72 1.22 9.23 3.6l6.9-6.9C36.54 2.5 30.7 0 24 0 14.64 0 6.53 5.4 2.56 13.32l8.06 6.26C12.7 13.09 17.9 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.1 24.5c0-1.6-.14-3.14-.4-4.62H24v9h12.44c-.54 2.9-2.2 5.36-4.68 7l7.2 5.6C43.93 37.1 46.1 31.3 46.1 24.5z"/>
              <path fill="#FBBC05" d="M10.62 28.58a14.5 14.5 0 0 1 0-9.16l-8.06-6.26A24.02 24.02 0 0 0 0 24c0 3.87.92 7.54 2.56 10.84l8.06-6.26z"/>
              <path fill="#34A853" d="M24 48c6.7 0 12.34-2.2 16.45-5.98l-7.2-5.6c-2 1.34-4.54 2.13-9.25 2.13-6.1 0-11.3-3.6-13.38-8.78l-8.06 6.26C6.53 42.6 14.64 48 24 48z"/>
            </svg>
            Continuar con Google
          </Button>
          <div className="my-4 flex w-full items-center justify-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-black/10 dark:to-white/10" />
              <span className="shrink-0 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                o
              </span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-black/10 dark:to-white/10" />
          </div> 
          </>
        )}
                
        {/* Email */}
        <div className="space-y-2">
          <div className="text-xs font-medium text-foreground/80">Email</div>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@correo.com"
            className={cn(
              'h-12 rounded-lg',
              'bg-white/60 dark:bg-white/5',
              'border border-black/5 dark:border-white/10',
              'focus-visible:ring-2 focus-visible:ring-black/10 dark:focus-visible:ring-white/15'
            )}
          />
        </div>

        {mode === 'password' ? (
          <>
            <div className="space-y-2">
              <div className="text-xs font-medium text-foreground/80">Contraseña</div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={cn(
                  'h-12 rounded-lg',
                  'bg-white/60 dark:bg-white/5',
                  'border border-black/5 dark:border-white/10',
                  'focus-visible:ring-2 focus-visible:ring-black/10 dark:focus-visible:ring-white/15'
                )}
              />
            </div>

            {pErr ? <div className="text-sm text-destructive">{pErr}</div> : null}

            <Button
            className="h-12 w-full rounded-lg"
            disabled={pLoading || !emailNorm || !password}
            type="submit"
            >
            {pLoading ? 'Entrando…' : 'Entrar'}
            </Button>

            <button
              type="button"
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition"
              onClick={() => {
                setMode('otp')
                setOtpSent(false)
                setOErr(null)
              }}
            >
              ¿Olvidaste tu password? Entra con OTP
            </button>
          </>
        ) : (
          <>
            {!otpSent ? (
              <>
                {oErr ? <div className="text-sm text-destructive">{oErr}</div> : null}

                <Button
                  className="h-12 w-full rounded-lg"
                  disabled={oLoading || !emailNorm}
                  type="submit"
                >
                  {oLoading ? 'Enviando…' : 'Enviar código'}
                </Button>

                <div className="pt-3 text-center text-xs text-muted-foreground">
                  Te llegará un código de 6 dígitos a tu correo.
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <div className="text-xs font-medium text-foreground/80">Código</div>
                  <OtpBoxes
                    value={code}
                    onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
                    disabled={oLoading}
                    theme={theme}
                  />
                </div>

                {oErr ? <div className="text-sm text-destructive">{oErr}</div> : null}

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="secondary"
                    className="h-12 rounded-lg"
                    disabled={oLoading}
                    onClick={() => {
                      setOtpSent(false)
                      setCode('')
                      setOErr(null)
                    }}
                  >
                    Cambiar
                  </Button>
                  <Button
                    className="h-12 rounded-lg"
                    disabled={oLoading || code.length !== 6}
                    type="submit"
                  >
                    {oLoading ? 'Verificando…' : 'Verificar'}
                  </Button>
                </div>

                <button
                  type="button"
                  className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition"
                  onClick={startOtp}
                  disabled={oLoading || !emailNorm}
                >
                  Reenviar código
                </button>
              </>
            )}
          </>
        )}
      </form>
    </div>
  )
}
